import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

interface InfoTipProps {
  /** Explanation shown in the tooltip. */
  text: string;
  /** Optional bold title line above the text. */
  title?: string;
  className?: string;
}

const TIP_WIDTH = 260;
const GAP = 8;
const MARGIN = 8;

/**
 * Small "i" icon that reveals an explanation on hover, keyboard focus, or tap.
 * Rendered in a portal with fixed positioning so it is never clipped by an
 * `overflow-x-auto` table wrapper and doesn't inherit the header's uppercase
 * styling.
 */
const InfoTip: React.FC<InfoTipProps> = ({ text, title, className = "" }) => {
  const id = useId();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; below: boolean } | null>(null);

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.min(TIP_WIDTH, window.innerWidth - MARGIN * 2);
    const left = Math.min(
      Math.max(MARGIN, r.left + r.width / 2 - width / 2),
      window.innerWidth - width - MARGIN,
    );
    // Prefer showing below the icon; flip above when close to the bottom edge.
    const below = r.bottom + GAP + 120 < window.innerHeight;
    setPos({ top: below ? r.bottom + GAP : r.top - GAP, left, below });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-describedby={open ? id : undefined}
        aria-label={title ? `About ${title}` : "More information"}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={`inline-flex items-center align-middle text-gray-400 hover:text-gray-600 focus:text-gray-600 focus:outline-none ${className}`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            id={id}
            role="tooltip"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: Math.min(TIP_WIDTH, window.innerWidth - MARGIN * 2),
              transform: pos.below ? undefined : "translateY(-100%)",
            }}
            className="z-[1000] pointer-events-none rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal normal-case tracking-normal leading-relaxed text-white shadow-lg"
          >
            {title && <p className="mb-0.5 font-semibold">{title}</p>}
            {text}
          </div>,
          document.body,
        )}
    </>
  );
};

export default InfoTip;
